import { useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  createSearchParams,
} from "react-router-dom";

import { IAppState } from "../core/redux/IAppState";
import { defaultMergeProps } from "../core/redux/sugar";
import { getEntitlementTypesAction } from "../entitlementTypes/getEntitlementTypesAction";
import { guestSearchAction } from "./guestSearchAction";
import { GuestSearchResults } from "./GuestSearchResults";

function GuestSearchResultsWrapper(props: any) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ query: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // v6 Router Navigation
  const history = useMemo(
    () => ({
      replace: (to: string) => navigate(to, { replace: true }),
      push: (to: string) => navigate(to),
    }),
    [navigate]
  );

  // Current sort settings from URL
  const sortBy = searchParams.get("sortBy") ?? undefined;
  const sortAsc = (searchParams.get("sortAsc") ?? "false") === "true";

  // Update sorting using v6 API; keeps other query params
  const setSort = useCallback(
    (nextSortBy?: string, nextSortAsc?: boolean) => {
      if (!nextSortBy) {
        const next = createSearchParams(searchParams);
        next.delete("sortBy");
        next.delete("sortAsc");
        setSearchParams(next, { replace: true });
        return;
      }
      const next = createSearchParams(searchParams);
      next.set("sortBy", nextSortBy);
      next.set("sortAsc", String(!!nextSortAsc));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return (
    <GuestSearchResults
      {...props}
      location={location}
      history={history}
      params={params}
      sortBy={sortBy}
      sortAsc={sortAsc}
      setSort={setSort}
      rawSearch={`?${searchParams.toString()}`}
    />
  );
}

export default connect(
  (state: IAppState) => ({
    isLoading: state.guestSearchResults?.isLoading,
    resultsQuery: state.guestSearchResults?.query,
    results: state.guestSearchResults?.results,
    error: state.guestSearchResults?.error,
    allEntitlementTypes: state.entitlementTypesDetails?.allEntitlementTypes,
    isLoadingEntitlementTypes: state.entitlementTypesDetails?.isLoading,
    selectedResort: state.selectedResort,
    resources: state.locale,
  }),
  (dispatch: Dispatch) => ({
    findGuest: (query: string) => dispatch(guestSearchAction(query, new Date())),
    fetchEntitlementTypes: () => dispatch(getEntitlementTypesAction()),
  }),
  defaultMergeProps,
  {
    areStatesEqual: (prev: IAppState, next: IAppState) =>
      prev.guestSearchResults === next.guestSearchResults &&
      prev.entitlementTypesDetails === next.entitlementTypesDetails &&
      prev.selectedResort === next.selectedResort,
  }
)(GuestSearchResultsWrapper);
